// Copy for EN/FR. Edit mission, vision, values, team, and culture here.

const SERVICES_DATA = [
  { image: '/images/data-visual.jpg' },
  { image: '/images/services-visual.jpg' },
  { image: '/images/insight-visual.jpg' },
  { image: '/images/hero-accent.jpg' },
  { image: '/images/insight-visual.jpg' },
  { image: '/images/data-visual.jpg' },
];

// Client logos: add PNG/SVG files to public/images/clients/ with these filenames
const CLIENTS = [
  { name: 'Sky', logo: '/images/clients/sky.png' },
  { name: 'Centrica', logo: '/images/clients/centrica.png' },
  { name: 'Very', logo: '/images/clients/very.png' },
  { name: 'Gymshark', logo: '/images/clients/gymshark.png' },
  { name: 'Alshaya Group', logo: '/images/clients/alshaya-group.png', logoClass: 'clients-logo-large' },
  { name: 'Minority Rights Group', logo: '/images/clients/minority-rights-group.png' },
  { name: 'KPMG', logo: '/images/clients/kpmg.png' },
  { name: 'Meta', logo: '/images/clients/meta.png' },
  { name: 'Together', logo: '/images/clients/together.png' },
  { name: 'Maids.cc', logo: '/images/clients/maids-cc.png' },
  { name: 'Adnoc Group', logo: '/images/clients/adnoc-group.png' },
];

export const content = {
  en: {
    nav: { services: 'Services', why: 'Why Basira', mission: 'Mission', team: 'Team', contact: 'Contact' },
    clientsBanner: 'We delivered value for',
    clients: CLIENTS,
    hero: {
      tagline: 'A Data & AI consultancy built on insight and experience.',
      desc: 'We help organisations build and sustain their data and AI strategy, with honesty, simplicity and ambition.',
      ctaPrimary: 'Get in touch',
      ctaSecondary: 'Our services',
    },
    services: {
      title: 'What we do',
      lead: 'Whether you are starting your Data and AI journey or looking to pivot it, we support you through every phase of the cycle so you can lead your business with confidence and intention.',
      items: [
        {
          title: 'Data Strategy & Governance',
          kicker: 'Strategy',
          strapline: 'A Clear Data Strategy For An Optimised Business — From Investment To Governance Frameworks.',
          description: 'A clear data strategy for an optimised business. We help you decide everything from the size of the investment, and the structure to your governance frameworks, to create a personalized strategy that aligns with your business goals and scale with your ambition.',
        },
        {
          title: 'Data Platform Implementation',
          kicker: 'Platform',
          strapline: 'From Cloud Warehouses To Lakehouses And Pipelines — Built To Scale And Stay Secure.',
          description: 'From cloud data warehouses to lakehouses and pipelines. We design and deliver scalable, secure data platforms so your organisation can ingest, store, and serve data reliably for analytics and AI.',
        },
        {
          title: 'Analytics & Insights',
          kicker: 'Analytics',
          strapline: 'Modern Stacks, Dashboards, And Deep Studies So Teams Act On Evidence, Not Intuition.',
          description: 'Data that delivers answers. We implement modern analytics stacks, dashboards, and self-serve reporting so your teams can act on evidence, not intuition. We use deep dive analytical studies to help you optimise your P&L, drive growth and find efficiencies across your business.',
        },
        {
          title: 'AI & Machine Learning',
          kicker: 'Intelligence',
          strapline: 'From Proof-Of-Concept To Production — Predictive Models, LLMs, And Intelligent Automation.',
          description: 'From proof-of-concept to production. We build and deploy models that solve real day-to-day and structural problems—predictive analytics, LLMs implementation and intelligent automation.',
        },
        {
          title: 'Up-skilling & Enablement',
          kicker: 'Capability',
          strapline: 'Training, Process, And A Data-First Culture So Your Organisation Keeps Winning After We Step Back.',
          description: 'Build lasting data and AI capability within your teams. We train your people, refine your processes, and embed a data-first culture so your organisation keeps winning after we step back.',
        },
        {
          title: 'Culture Transformation',
          kicker: 'Culture',
          strapline: 'Leadership Alignment, Literacy Programmes, And Trust So Your Data Vision Becomes Reality.',
          description: 'Data and AI only deliver when people trust them and use them. We help you create an environment where your teams can overcome their fears and confusion about Data and AI so that your data vision can become reality— this includes everything from leadership alignment workshops to data literacy programmes design and activations.',
        },
      ],
    },
    mission: {
      title: 'Our mission, vision & values',
      missionTitle: 'Mission',
      missionText: 'To enable every organisation with data and AI capabilities that empowers their teams and aligns with their goals.',
      visionTitle: 'Vision',
      visionText: 'A world where every organisation uses data & insight—بصيرة—as a strategic advantage: evidence-led, inclusive, and sustainable.',
      valuesTitle: 'Values',
      values: [
        { title: 'Tailored over hype.', text: 'Shiny new tools might be on the market, but we will always advise you based on what we know works best for you.' },
        { title: 'Sustainability over speed.', text: 'We will always design with tomorrow in mind, so you can continue to use our work long after we are gone.' },
        { title: 'Global in the service of Local.', text: 'In a world so interconnected, we believe global examples and use cases can bring the breakthrough you’ve been looking for.' },
        { title: 'Integrity over business.', text: 'We believe in doing the right thing when it comes to data and AI, from strategy to tools and data access—we will always choose to share honest advice in true partnership.' },
      ],
    },
    why: {
      title: 'Why Basira',
      lead: 'Insight—<span lang="ar">بصيرة</span>—is at the heart of what we do. We combine analytical rigour with practical delivery so your investments in data and AI pay off.',
      list: [
        { strong: 'Rigorous thinking.', text: 'We question assumptions, stress-test options, and base recommendations on evidence—not hype.' },
        { strong: 'Execution that sticks.', text: 'We design for your context and capability, so solutions are adopted and sustained long after we leave.' },
        { strong: 'Clear communication.', text: 'We translate complexity into decisions: no jargon, no black boxes, just clarity you can act on.' },
      ],
    },
    culture: {
      title: 'Culture transformation',
      lead: 'Data and AI only deliver when people trust them and use them. We help you build the mindset, skills, and habits that make change stick.',
      points: [
        'Leadership alignment and sponsorship',
        'Data literacy and analytics capability at scale',
        'Governance that enables rather than blocks',
        'Inclusive design so insights serve everyone',
      ],
    },
    team: {
      title: 'Meet the team',
      lead: 'We are practitioners and advisors who have led Data, AI & Transformation programmes in industry. We bring both depth and pragmatism to every engagement.',
      members: [
        {
          name: 'Chady Younan',
          role: 'Founder / Chief Data, Analytics & AI Officer',
          bio: 'Chady is the founder of Basira and a seasoned Chief Data & AI Officer with over 15 years of experience building and leading Data and AI programmes. He has deep expertise in analytics, machine learning, and turning Data into strategy—across all industries.',
          photo: '/images/team/chady-younan.jpg',
        },
        {
          name: 'Sarah Elashmawy',
          role: 'Founder / Head of Culture & Growth',
          bio: 'Sarah leads culture and growth at Basira. She brings over 13 years of experience in strategy, partnerships, and organisational transformation from roles at Meta, Together, and KPMG. A culture and strategy consultant with an MSc from SOAS, she works in English, French, and Arabic.',
          photo: '/images/team/sarah-elashmawy.jpg',
        },
      ],
    },
    contact: {
      title: "Let's talk",
      lead: "Whether you're shaping a data strategy, scaling AI, or building analytics capability—we'd like to hear from you.",
      enquiryButton: 'Contact Us',
      enquirySubject: 'Enquiry — Basira Data & AI services',
      enquiryBody:
        'Hello Sarah,\n\nI would like to find out more about Basira and how you could support our organisation with data and AI.\n\n[Add your context or questions here.]\n\nBest regards,',
      domain: 'basira.me',
    },
    footer: 'Data & AI consultancy.',
  },
  fr: {
    nav: { services: 'Services', why: 'Pourquoi Basira', mission: 'Mission', team: 'Équipe', contact: 'Contact' },
    clientsBanner: 'Nous avons apporté de la valeur à',
    clients: CLIENTS,
    hero: {
      tagline: 'Un cabinet Data & IA fondé sur l’insight et l’expérience.',
      desc: 'Nous aidons les organisations à construire et pérenniser leur stratégie data et IA, avec honnêteté, simplicité et ambition.',
      ctaPrimary: 'Nous contacter',
      ctaSecondary: 'Nos services',
    },
    services: {
      title: 'Ce que nous faisons',
      lead: 'Que vous débutiez votre parcours Data et IA ou que vous souhaitiez le réorienter, nous vous accompagnons à chaque phase du cycle pour que vous puissiez piloter votre entreprise avec confiance et intention.',
      items: [
        {
          title: 'Stratégie & gouvernance des données',
          kicker: 'Stratégie',
          strapline: 'Une Stratégie Data Claire Pour Une Entreprise Optimisée — Des Investissements Aux Cadres De Gouvernance.',
          description: 'Une stratégie data claire pour une entreprise optimisée. Nous vous aidons à décider de tout : montant des investissements, structure et cadres de gouvernance, pour créer une stratégie sur mesure alignée sur vos objectifs et votre ambition.',
        },
        {
          title: 'Mise en œuvre de plateforme data',
          kicker: 'Plateforme',
          strapline: 'Des Entrepôts Cloud Aux Lakehouses Et Pipelines — Scalable, Sécurisé, Fiable Pour L’analytique Et L’IA.',
          description: 'Des entrepôts de données cloud aux lakehouses et pipelines. Nous concevons et livrons des plateformes data scalables et sécurisées pour que votre organisation ingère, stocke et serve la donnée de façon fiable pour l’analytique et l’IA.',
        },
        {
          title: 'Analytics & insights',
          kicker: 'Analytique',
          strapline: 'Stacks Modernes, Tableaux De Bord Et Études Approfondies Pour Décider Sur Les Faits, Pas L’intuition.',
          description: 'Des données qui apportent des réponses. Nous mettons en place des stacks analytiques modernes, tableaux de bord et reporting en self-service pour que vos équipes s’appuient sur les faits. Nous réalisons des études analytiques approfondies pour vous aider à optimiser votre P&L, stimuler la croissance et dégager des gains d’efficacité.',
        },
        {
          title: 'IA & Machine Learning',
          kicker: 'Intelligence',
          strapline: 'Du Proof-Of-Concept À La Production — Modèles Prédictifs, LLM Et Automatisation Intelligente.',
          description: 'Du proof-of-concept à la production. Nous construisons et déployons des modèles qui résolvent des problèmes concrets au quotidien et structurels—analytique prédictive, mise en œuvre de LLM et automatisation intelligente.',
        },
        {
          title: 'Montée en compétence & enablement',
          kicker: 'Capacité',
          strapline: 'Formation, Processus Et Culture Data-First Pour Que Votre Organisation Continue À Gagner Après Notre Départ.',
          description: 'Construire une capacité data et IA durable au sein de vos équipes. Nous formons vos collaborateurs, optimisons vos processus et ancrons une culture data-first pour que votre organisation continue à gagner après notre départ.',
        },
        {
          title: 'Transformation culturelle',
          kicker: 'Culture',
          strapline: 'Alignement Du Leadership, Littératie Data Et Confiance Pour Que Votre Vision Data Devienne Réalité.',
          description: 'La donnée et l’IA ne portent leurs fruits que lorsque les personnes leur font confiance. Nous vous aidons à créer un environnement où vos équipes peuvent surmonter leurs craintes et leur confusion face à la Data et à l’IA pour que votre vision data devienne réalité—ateliers d’alignement du leadership, conception et déploiement de programmes de littératie data.',
        },
      ],
    },
    mission: {
      title: 'Notre mission, vision & valeurs',
      missionTitle: 'Mission',
      missionText: 'Donner à chaque organisation des capacités data et IA qui renforcent ses équipes et s’alignent sur ses objectifs.',
      visionTitle: 'Vision',
      visionText: 'Un monde où chaque organisation utilise la donnée et l’insight—بصيرة—comme avantage stratégique : fondé sur les preuves, inclusif et durable.',
      valuesTitle: 'Valeurs',
      values: [
        { title: 'Sur mesure plutôt que l’effet de mode.', text: 'Les outils à la mode pullulent, mais nous vous conseillerons toujours en fonction de ce qui fonctionne le mieux pour vous.' },
        { title: 'Durabilité plutôt que vitesse.', text: 'Nous concevons toujours en pensant à demain, pour que vous puissiez continuer à utiliser notre travail longtemps après notre départ.' },
        { title: 'Le global au service du local.', text: 'Dans un monde si interconnecté, nous croyons que les exemples et cas d’usage globaux peuvent apporter le déclic que vous cherchez.' },
        { title: 'Intégrité plutôt que l’affaire.', text: 'Nous croyons qu’il faut faire ce qui est juste en matière de data et d’IA, de la stratégie aux outils et à l’accès aux données—nous choisirons toujours de partager un conseil honnête dans un vrai partenariat.' },
      ],
    },
    why: {
      title: 'Pourquoi Basira',
      lead: 'L’insight—<span lang="ar">بصيرة</span>—est au cœur de notre approche. Nous allions rigueur analytique et mise en œuvre concrète pour que vos investissements data et IA portent leurs fruits.',
      list: [
        { strong: 'Pensée rigoureuse.', text: 'Nous questionnons les hypothèses, stressons les options et fondons nos recommandations sur les preuves—pas le battage médiatique.' },
        { strong: 'Une exécution qui dure.', text: 'Nous concevons pour votre contexte et vos capacités, pour que les solutions soient adoptées et maintenues bien après notre départ.' },
        { strong: 'Communication claire.', text: 'Nous traduisons la complexité en décisions : pas de jargon, pas de boîtes noires, juste de la clarté actionnable.' },
      ],
    },
    culture: {
      title: 'Transformation culturelle',
      lead: 'La donnée et l’IA ne portent leurs fruits que lorsque les personnes leur font confiance et les utilisent. Nous vous aidons à développer les mentalités, compétences et habitudes qui font durer le changement.',
      points: [
        'Alignement et sponsorship du leadership',
        'Littératie data et capacité analytique à l’échelle',
        'Une gouvernance qui permet au lieu de bloquer',
        'Conception inclusive pour des insights au service de tous',
      ],
    },
    team: {
      title: 'L’équipe',
      lead: 'Nous sommes des praticiens et conseillers qui ont piloté des programmes Data, IA & Transformation en entreprise. Nous apportons profondeur et pragmatisme à chaque mission.',
      members: [
        {
          name: 'Chady Younan',
          role: 'Founder / Chief Data, Analytics & AI Officer',
          bio: 'Chady est le fondateur de Basira et un Chief Data & AI Officer chevronné avec plus de 15 ans d’expérience dans la conception et le pilotage de programmes Data et IA. Il possède une expertise reconnue en analytique, apprentissage automatique et transformation de la donnée en stratégie—tous secteurs confondus.',
          photo: '/images/team/chady-younan.jpg',
        },
        {
          name: 'Sarah Elashmawy',
          role: 'Founder / Head of Culture & Growth',
          bio: 'Sarah pilote la culture et la croissance chez Basira. Elle apporte plus de 13 ans d’expérience en stratégie, partenariats et transformation organisationnelle, après des postes chez Meta, Together et KPMG. Consultante en culture et stratégie, diplômée d’un MSc du SOAS, elle intervient en anglais, français et arabe.',
          photo: '/images/team/sarah-elashmawy.jpg',
        },
      ],
    },
    contact: {
      title: 'Parlons-en',
      lead: 'Que vous définissiez une stratégie data, déployiez l’IA ou renforciez vos capacités analytiques—nous aimerions échanger avec vous.',
      enquiryButton: 'Contactez-Nous',
      enquirySubject: 'Demande d’informations — services Basira Data & IA',
      enquiryBody:
        'Bonjour Sarah,\n\nJe souhaite en savoir plus sur Basira et sur la façon dont vous pourriez accompagner notre organisation en data et IA.\n\n[Ajoutez votre contexte ou vos questions ici.]\n\nCordialement,',
      domain: 'basira.me',
    },
    footer: 'Cabinet Data & IA.',
  },
};

export { SERVICES_DATA };
